/**
 * Helpers for auto-generating short, unique, human-friendly codes from a name.
 * Used by administration forms where the code is derived from the name but
 * remains editable.
 */

/**
 * Build a short uppercase acronym from a human name.
 *
 *   generateAcronymCode('Complete Blood Count') -> 'CBC'
 *   generateAcronymCode('OPD Consultation')     -> 'OC'
 *   generateAcronymCode('X-Ray')                -> 'XRAY'
 *   generateAcronymCode('Syringe 5ml')          -> 'SYRI'
 *
 * A one or two character acronym is rarely useful, so names with too few
 * words fall back to the first few letters of the name.
 */
export function generateAcronymCode(name: string, maxLength = 20): string {
  const words = name
    .replace(/[^A-Za-z0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  let acronym = words.map((word) => word[0]).join('').toUpperCase();

  if (acronym.length < 3) {
    acronym = name.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4);
  }

  return acronym.slice(0, maxLength);
}

/**
 * Return `base` (uppercased) with a numeric suffix appended until it no longer
 * collides with any of `existingCodes`.
 *
 *   makeUniqueCode('CBC', ['CBC'])       -> 'CBC-2'
 *   makeUniqueCode('CBC', ['CBC','CBC-2']) -> 'CBC-3'
 */
export function makeUniqueCode(
  base: string,
  existingCodes: Iterable<string | null | undefined>,
  options: { maxLength?: number; separator?: string; start?: number } = {},
): string {
  const { maxLength = 20, separator = '-', start = 2 } = options;

  const taken = new Set(
    Array.from(existingCodes, (code) => String(code ?? '').trim().toUpperCase()).filter(Boolean),
  );

  const trimmedBase = base.slice(0, maxLength).toUpperCase();
  if (!trimmedBase) return '';
  if (!taken.has(trimmedBase)) return trimmedBase;

  let n = start;
  // Bound the loop defensively; the suffix always terminates long before this.
  for (let guard = 0; guard < 10000; guard += 1) {
    const suffix = `${separator}${n}`;
    const head = trimmedBase.slice(0, Math.max(1, maxLength - suffix.length));
    const candidate = `${head}${suffix}`;
    if (!taken.has(candidate)) return candidate;
    n += 1;
  }

  return trimmedBase;
}

/**
 * Build a lower-case snake_case code from a human name.
 *
 *   generateSnakeCode('Lab Test')       -> 'lab_test'
 *   generateSnakeCode('Bed Allocation') -> 'bed_allocation'
 *   generateSnakeCode('X-Ray')          -> 'x_ray'
 *
 * Used by master-data forms (e.g. Service Types) whose codes are stored as
 * lower-case snake_case identifiers.
 */
export function generateSnakeCode(name: string, maxLength = 50): string {
  return name
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()
    .slice(0, maxLength)
    .replace(/_+$/g, '');
}

/**
 * Return a lower-case snake_case `base` with a numeric suffix appended until it
 * no longer collides with any of `existingCodes` (case-insensitive).
 *
 *   makeUniqueSnakeCode('lab_test', ['lab_test'])          -> 'lab_test_2'
 *   makeUniqueSnakeCode('lab_test', ['lab_test','lab_test_2']) -> 'lab_test_3'
 */
export function makeUniqueSnakeCode(
  base: string,
  existingCodes: Iterable<string | null | undefined>,
  options: { maxLength?: number; separator?: string; start?: number } = {},
): string {
  const { maxLength = 50, separator = '_', start = 2 } = options;

  const taken = new Set(
    Array.from(existingCodes, (code) => String(code ?? '').trim().toLowerCase()).filter(Boolean),
  );

  const trimmedBase = base.slice(0, maxLength).toLowerCase().replace(/_+$/g, '');
  if (!trimmedBase) return '';
  if (!taken.has(trimmedBase)) return trimmedBase;

  let n = start;
  for (let guard = 0; guard < 10000; guard += 1) {
    const suffix = `${separator}${n}`;
    const head = trimmedBase
      .slice(0, Math.max(1, maxLength - suffix.length))
      .replace(/_+$/g, '');
    const candidate = `${head}${suffix}`;
    if (!taken.has(candidate)) return candidate;
    n += 1;
  }

  return trimmedBase;
}
